import * as fabric from "fabric";
import initAligningGuidelines from "@/utils/canvas/aligningGuidelines";
import initCenteringGuidelines from "@/utils/canvas/centeringGuidelines";
import initCustomControls from "@/utils/canvas/customControls";
import doubleClickSelect from "@/utils/helpers/doubleClickSelect";
import loadCrossOriginImage from "@/utils/helpers/loadCrossOriginImage";
import updateAllObjectsCrossOrigin from "@/utils/helpers/updateAllObjectsCrossOrigin";
import { canvasWidth, maxDisplayWidth, canvasAspectRatio } from "@/constants/canvas";
import cardConfig from "@/config/cardConfig";
import { nextTick } from "vue";
import FontFaceObserver from "@/utils/fontObserver";

/**
 * Canvas Setup Mixin
 * Handles canvas initialization and setup
 */
export default {
  methods: {
    getTopMargin() {
      return this.getCanvasWidth() / canvasAspectRatio + 182 + "px";
    },

    getCanvasWidth(containerEl) {
      // 16px spacing on each side (32 total) at all breakpoints
      const padding = 32;

      if (containerEl) {
        const availableW = containerEl.clientWidth - padding;
        const availableH = containerEl.clientHeight - padding;
        const widthFromHeight = availableH * canvasAspectRatio;
        return Math.max(200, Math.min(availableW, widthFromHeight, maxDisplayWidth));
      }

      return Math.min(document.body.clientWidth - padding, maxDisplayWidth);
    },

    addGlobals() {
      window.fabric.IText.prototype.padding = 0;
      window.fabric.FabricObject.prototype.set("snapAngle", 45);
      window.fabric.FabricObject.prototype.set("snapThreshold", 10);
      window.fabric.FabricObject.prototype.set("lockScalingFlip", true);
    },

    async createFrontSide(onlyInitializeCanvas) {
      if (!this.config.frontConfig) return;

      const { canvas, helperCanvas, chip } = this.config.frontConfig;

      window.frontCanvas = new window.fabric.Canvas("frontEditor", {
        ...canvas,
      });

      window.helperCanvas = new window.fabric.Canvas("helperCanvas", {
        ...helperCanvas,
      });

      window.helperCanvas.on({
        "selection:cleared": () => {
          window?.activeCanvas?.discardActiveObject();
          window?.activeCanvas?.renderAll();
        },
      });

      window.frontCanvas.setZoom(window.frontCanvas.getWidth() / canvasWidth);
      window.helperCanvas.setZoom(window.frontCanvas.getWidth() / canvasWidth);

      window.frontCanvas.on({
        "selection:created": this.onSelectionChange,
        "selection:updated": this.onSelectionChange,
        "selection:cleared": this.onSelectionCleared,
        "object:rotating": this.onRotate,
        "text:changed": this.onTextChange,
        "object:added": ({ target }) => {
          if (target && target.type?.toLowerCase().includes("text")) {
            doubleClickSelect(target);
          }
          initCustomControls(target, this);
        },
      });

      window.activeCanvas = window.frontCanvas;

      initAligningGuidelines(window.helperCanvas);
      initCenteringGuidelines(window.helperCanvas);
      initAligningGuidelines(window.frontCanvas);
      initCenteringGuidelines(window.frontCanvas);
    },

    async addDefaultChip() {
      const chip = this.config?.frontConfig?.chip;
      if (!chip) return;

      try {
        const chipImage = await loadCrossOriginImage(chip.src);
        const img = new window.fabric.FabricImage(chipImage);
        img.set({
          ...chip,
        });

        window.chipElement = img;
        window.frontCanvas.add(img);

        // Simple function to keep chip on top
        const keepChipOnTop = () => {
          if (window.chipElement && window.frontCanvas) {
            window.frontCanvas.bringObjectToFront(window.chipElement);
          }
        };

        // Listen to key events and keep chip on top
        window.frontCanvas.on('object:added', keepChipOnTop);

        window.frontCanvas.renderAll();
      } catch (error) {
        console.error('Failed to add chip:', error);
      }
    },

    async createBackSide(onlyInitializeCanvas) {
      if (!this.config.backConfig) return;

      const { canvas } = this.config.backConfig;

      window.backCanvas = new window.fabric.Canvas("backEditor", {
        ...canvas,
      });

      window.backCanvas.setZoom(window.backCanvas.getWidth() / canvasWidth);

      window.backCanvas.on({
        "selection:created": this.onSelectionChange,
        "selection:updated": this.onSelectionChange,
        "selection:cleared": this.onSelectionCleared,
        "object:rotating": this.onRotate,
        "text:changed": this.onTextChange,
        "object:added": ({ target }) => {
          if (target && target.type?.toLowerCase().includes("text")) {
            doubleClickSelect(target);
          }
          initCustomControls(target, this);
        },
      });

      initAligningGuidelines(window.backCanvas);
      initCenteringGuidelines(window.backCanvas);
    },

    async initializeCanvases(onlyInitializeCanvas, data = {}) {
      try {
        this.$refs.loader.show(this.$t("loading"));

        this.config = cardConfig();

        await nextTick();

        await this.createFrontSide(onlyInitializeCanvas);

        await this.createBackSide(onlyInitializeCanvas);

        this.handleAppResize();

        this.$refs.loader.hide();

        // Load fonts in background after UI is shown
        for (let i = 0; i < this.fontsToLoad.length; i++) {
          const currentFont = this.fontsToLoad[i];
          const font = new FontFaceObserver(currentFont);
          font.load().catch(err => {
            console.log(err);
            console.warn(`Could not load font ${currentFont}`)
          });
        }

        if (data && data.front) {
          await nextTick();

          updateAllObjectsCrossOrigin(data.front.objects);
          await window.frontCanvas.loadFromJSON(data.front);

          window.frontCanvas.getObjects().forEach((obj) => {
            if (obj?.type && obj?.type?.toLowerCase()?.includes("text")) {
              obj.set({
                objectCaching: false,
                top: Math.max(obj.top, 0),
                lineHeight: 1.1,
                fontFamily: obj.fontFamily,
              });
              obj._clearCache();
              obj.initDimensions();
              obj.setCoords();
            }
          });

          window.frontCanvas.requestRenderAll();

          if (data && data.back) {
            updateAllObjectsCrossOrigin(data.back.objects);
            await window.backCanvas.loadFromJSON(data.back);

            window.backCanvas.getObjects().forEach((obj) => {
              if (obj.type?.toLowerCase().includes("text")) {
                obj.set({
                  objectCaching: false,
                  top: Math.max(obj.top, 0),
                  lineHeight: 1.1,
                });
                obj._clearCache();
                obj.initDimensions();
                obj.setCoords();
              }
            });

            window.backCanvas.requestRenderAll();
          }
        }

        // Add default chip after all data is loaded
        await this.addDefaultChip();

        // Live preview for side toggle: listen to canvas changes and update preview thumbnails
        if (typeof this.setupPreviewListeners === "function") {
          this.setupPreviewListeners();
        }
        if (typeof this.updatePreviews === "function") {
          this.updatePreviews();
        }

        // Don't auto-show in addToCart mode - wait for explicit showEditor() call
        // Only auto-initialize in save mode or standalone mode
        if (this.editorMode === 'save' || !window.__designerConfig?.isLibraryMode) {
          setTimeout(() => {
            this.initialized = true;
            this.$nextTick(() => {
              if (typeof this.checkCardOverflow === "function") {
                this.checkCardOverflow();
              }
            });
          }, 100);
        } else {
          // Mark that canvas is ready but keep it hidden
          window.__canvasReady = true;
        }
      } catch (error) {
        console.error("Canvas initialization error:", error);
        this.$refs.loader.hide();
      }
    },
  },
};
